import { ValueType } from '@aloft/toolkit-shared';

import { DetailField, SalesComparisonColumns } from './types.ts';

export const salesComparisonColumns: SalesComparisonColumns = [
  { key: ['dataSource'], label: ['Data Source'] },
  { key: ['proximity'], label: ['Proximity'] },
  { key: ['yearBuilt'], label: ['Year Built'] },
  { key: ['salesPrice'], label: ['Sales Price'] },
  { key: ['listingPrice'], label: ['Listing Price'] },
  { key: ['dom'], label: ['DOM'] },
  {
    key: ['contractDate', 'saleDate'],
    label: ['Contract Date', 'Sale Date'],
    adjustment: 'saleDateAdjustment',
  },
  {
    key: ['siteSize', 'siteInfluence', 'view'],
    label: ['Site Size', 'Site Influence', 'View'],
    adjustment: 'siteSizeAdjustment',
  },
  { key: ['quality'], label: ['Quality'], adjustment: 'qualityAdjustment' },
  { key: ['condition'], label: ['Condition'], adjustment: 'conditionAdjustment' },
  { key: ['gla'], label: ['GLA'], adjustment: 'glaAdjustment' },
  { key: ['bedCount'], label: ['Bed Count'], adjustment: 'bedCountAdjustment' },
  { key: ['bathCount'], label: ['Bath Count'], adjustment: 'bathCountAdjustment' },
  { key: ['basement'], label: ['Basement'], adjustment: 'basementAdjustment' },
  { key: ['garage'], label: ['Garage / Carport'], adjustment: 'garageAdjustment' },
  { key: ['porch'], label: ['Porch / Patio / Deck'], adjustment: 'porchAdjustment' },
  { key: ['attic'], label: ['Attic'], adjustment: 'atticAdjustment' },
  { key: ['fireplace'], label: ['Fireplace'], adjustment: 'fireplaceAdjustment' },
  { key: ['shed'], label: ['Shed'], adjustment: 'shedAdjustment' },
  { key: ['barn'], label: ['Barn'], adjustment: 'barnAdjustment' },
  { key: ['adu'], label: ['ADU'], adjustment: 'aduAdjustment' },
  { key: ['pool'], label: ['Pool / Spa'], adjustment: 'poolAdjustment' },
  { key: ['workshop'], label: ['Workshop'], adjustment: 'workshopAdjustment' },
  {
    key: ['netAdj', 'grossAdj'],
    label: ['Net Adj.', 'Gross Adj.'],
    adjustment: 'netAdjPrice',
    borderTop: true,
  },
  {
    key: ['adjSalesPrice'],
    label: ['Adj. Sales Price'],
    bold: true,
    borderTop: true,
    align: 'right',
  },
];

export const ADDITIONAL_FEATURE_KEYS = [
  'basement',
  'garage',
  'porch',
  'attic',
  'fireplace',
  'pool',
  'shed',
  'barn',
  'adu',
  'workshop',
];

export const ADDITIONAL_FEATURE_LABELS = {
  basement: 'Basement',
  garage: 'Garage / Carport',
  porch: 'Porch / Patio / Deck',
  attic: 'Attic',
  fireplace: 'Fireplace',
  pool: 'Pool / Spa',
  shed: 'Shed',
  barn: 'Barn',
  adu: 'ADU',
  workshop: 'Workshop',
};

export const propertyDetailFields = (
  address?: string,
  propertyType?: string,
  zoning?: string,
  zoningCompliance?: string,
  parcelNumber?: string,
  newConstruction?: boolean,
  propertyInterest?: string,
  hoa?: string,
  hoaFees?: string,
  pud?: string,
  legalDescription?: string,
): DetailField[] => [
  { key: 'address', label: 'Subject Property Address', getValue: () => address },
  { key: 'propertyType', label: 'Property Type', getValue: () => propertyType },
  { key: 'zoning', label: 'Zoning', getValue: () => zoning },
  { key: 'zoningCompliance', label: 'Zoning Compliance', getValue: () => zoningCompliance },
  {
    key: 'parcelNumber',
    label: 'Parcel Number',
    getValue: () => (newConstruction ? 'New Construction' : parcelNumber),
  },
  {
    key: 'propertyInterest',
    label: 'Property Interest',
    getValue: () => propertyInterest,
  },
  { key: 'hoa', label: 'Neighborhood Name', getValue: () => hoa },
  { key: 'hoaFees', label: 'Mandatory Fees', getValue: () => hoaFees },
  { key: 'pud', label: 'PUD', getValue: () => pud },
  { key: 'legalDescription', label: 'Legal Description', getValue: () => legalDescription },
];

export const orderDetailFields = (
  clientName?: string,
  clientAddress?: string,
  borrowerName?: string,
  appraisalType?: string,
  orderNumber?: string,
  inspectionDate?: string,
  effectiveDate?: string,
  intendedUsers?: string,
  intendedUse?: string,
): DetailField[] => [
  { key: 'clientName', label: 'Client Name', getValue: () => clientName },
  { key: 'clientAddress', label: 'Client Address', getValue: () => clientAddress },
  { key: 'borrowerName', label: 'Borrower Name', getValue: () => borrowerName },
  { key: 'appraisalType', label: 'Product Type', getValue: () => appraisalType },
  { key: 'orderNumber', label: 'Order Number', getValue: () => orderNumber },
  { key: 'inspectionDate', label: 'Inspection Date', getValue: () => inspectionDate },
  { key: 'effectiveDate', label: 'Effective Date', getValue: () => effectiveDate },
  { key: 'intendedUsers', label: 'Intended User(s)', getValue: () => intendedUsers },
  { key: 'intendedUse', label: 'Intended Use', getValue: () => intendedUse },
];

export const valueTypeLabels: Record<ValueType, string> = {
  'as-is': 'As-Is',
  'subject-to': 'Subject-To',
};
